import { create } from 'zustand'
import { type GeofenceResponse, geolocationApi } from '@/services/api/geolocation'

export interface GeofenceVertex {
  longitude: number
  latitude: number
  height?: number
}

export interface Geofence {
  id: string
  name: string
  vertices: GeofenceVertex[]
  height: number
  infiniteHeight: boolean
  color: string
  visible: boolean
}

export interface BreachAlert {
  id: string // same as breachKey: "entityId:fenceId"
  entityName: string
  designator: string
  fenceName: string
  fenceColor: string
  timestamp: string // locale time string
}

export type DrawingMode = 'idle' | 'drawing'

interface GeofenceState {
  caseId: string | null
  geofences: Geofence[]
  drawingMode: DrawingMode
  drawingVertices: GeofenceVertex[]
  /** Live-edited name for the in-progress draw. The toolbar's Save button
   *  reads this when persisting the geofence. Reset on draw start/cancel. */
  drawingName: string
  pendingGeofence: Geofence | null
  defaultFenceHeight: number
  breachedPairs: Set<string>
  breachAlerts: BreachAlert[]

  setCaseId: (caseId: string) => void
  loadGeofences: (caseId: string) => Promise<void>
  startDrawing: () => void
  cancelDrawing: () => void
  setDrawingName: (name: string) => void
  addVertex: (vertex: GeofenceVertex) => void
  undoVertex: () => void
  completeDrawing: () => void
  saveDrawing: (name: string, height: number, infiniteHeight: boolean) => void
  confirmGeofence: (name: string, height: number, infiniteHeight: boolean) => void
  removeGeofence: (id: string) => void
  toggleGeofenceVisibility: (id: string) => void
  setDefaultFenceHeight: (h: number) => void
  addBreachedPair: (key: string) => void
  removeBreachedPair: (key: string) => void
  clearBreachedPairs: () => void
  addBreachAlert: (alert: BreachAlert) => void
  dismissBreachAlert: (id: string) => void
  clearBreachAlerts: () => void
}

const FENCE_COLORS = ['#f59e0b', '#ef4444', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899']

function mapResponseToGeofence(r: GeofenceResponse): Geofence {
  return {
    id: r.id,
    name: r.name,
    vertices: r.vertices,
    height: r.height,
    infiniteHeight: r.infiniteHeight,
    color: r.color,
    visible: r.visible,
  }
}

export const useGeofenceStore = create<GeofenceState>((set, get) => ({
  caseId: null,
  geofences: [],
  drawingMode: 'idle',
  drawingVertices: [],
  drawingName: '',
  pendingGeofence: null,
  defaultFenceHeight: 200,
  breachedPairs: new Set<string>(),
  breachAlerts: [],

  setCaseId: caseId => set({ caseId }),

  loadGeofences: async caseId => {
    try {
      const data = await geolocationApi.getGeofences(caseId)
      set({ caseId, geofences: data.map(mapResponseToGeofence) })
    } catch (err) {
      console.error('Failed to load geofences:', err)
    }
  },

  startDrawing: () => set({ drawingMode: 'drawing', drawingVertices: [], drawingName: '' }),

  cancelDrawing: () =>
    set({ drawingMode: 'idle', drawingVertices: [], drawingName: '', pendingGeofence: null }),

  setDrawingName: name => set({ drawingName: name }),

  addVertex: vertex => set(state => ({ drawingVertices: [...state.drawingVertices, vertex] })),

  undoVertex: () => set(state => ({ drawingVertices: state.drawingVertices.slice(0, -1) })),

  saveDrawing: (name, height, infiniteHeight) => {
    const state = get()
    if (state.drawingVertices.length < 3) return
    const colorIndex = state.geofences.length % FENCE_COLORS.length
    const color = FENCE_COLORS[colorIndex]
    const vertices = state.drawingVertices

    set({
      drawingMode: 'idle' as const,
      drawingVertices: [],
      drawingName: '',
      pendingGeofence: null,
    })

    const { caseId } = get()
    if (!caseId) return

    geolocationApi
      .createGeofence(caseId, { name, vertices, height, infiniteHeight, color })
      .then(resp => {
        set(s => ({ geofences: [...s.geofences, mapResponseToGeofence(resp)] }))
      })
      .catch(err => console.error('Failed to save geofence:', err))
  },

  completeDrawing: () =>
    set(state => {
      if (state.drawingVertices.length < 3) return state
      const colorIndex = state.geofences.length % FENCE_COLORS.length
      return {
        drawingMode: 'idle',
        pendingGeofence: {
          id: crypto.randomUUID(),
          name: '',
          vertices: state.drawingVertices,
          height: state.defaultFenceHeight,
          infiniteHeight: false,
          color: FENCE_COLORS[colorIndex],
          visible: true,
        },
        drawingVertices: [],
      }
    }),

  confirmGeofence: (name, height, infiniteHeight) => {
    const state = get()
    if (!state.pendingGeofence) return

    const pending = state.pendingGeofence
    set({ pendingGeofence: null })

    const { caseId } = get()
    if (!caseId) return

    geolocationApi
      .createGeofence(caseId, {
        name,
        vertices: pending.vertices,
        height,
        infiniteHeight,
        color: pending.color,
      })
      .then(resp => {
        set(s => ({ geofences: [...s.geofences, mapResponseToGeofence(resp)] }))
      })
      .catch(err => console.error('Failed to save geofence:', err))
  },

  removeGeofence: id => {
    set(state => ({
      geofences: state.geofences.filter(g => g.id !== id),
    }))

    const { caseId } = get()
    if (caseId) {
      geolocationApi
        .deleteGeofence(caseId, id)
        .catch(err => console.error('Failed to delete geofence:', err))
    }
  },

  toggleGeofenceVisibility: id => {
    let newVisible = false
    set(state => ({
      geofences: state.geofences.map(g => {
        if (g.id === id) {
          newVisible = !g.visible
          return { ...g, visible: newVisible }
        }
        return g
      }),
    }))

    const { caseId } = get()
    if (caseId) {
      geolocationApi
        .updateGeofence(caseId, id, { visible: newVisible } as any)
        .catch(err => console.error('Failed to update geofence visibility:', err))
    }
  },

  setDefaultFenceHeight: h => set({ defaultFenceHeight: h }),

  addBreachedPair: key =>
    set(state => {
      const next = new Set(state.breachedPairs)
      next.add(key)
      return { breachedPairs: next }
    }),

  removeBreachedPair: key =>
    set(state => {
      const next = new Set(state.breachedPairs)
      next.delete(key)
      return { breachedPairs: next }
    }),

  clearBreachedPairs: () => set({ breachedPairs: new Set<string>() }),

  addBreachAlert: alert => set(state => ({ breachAlerts: [alert, ...state.breachAlerts] })),

  dismissBreachAlert: id =>
    set(state => ({ breachAlerts: state.breachAlerts.filter(a => a.id !== id) })),

  clearBreachAlerts: () => set({ breachAlerts: [] }),
}))
